import { SearchOptions } from "@algolia/client-search"
import { SerpSettings } from "../../../../../contents/settings/serp/types"
import { toAlgoliaFilter } from "../../../../../features/algolia/api/filters"
import { SearchParameters } from "../../../../../features/algolia/api/types/search"
import {
  getAlgoliaClient,
  PRODUCTS_INDEX_NAME,
} from "../../../../../features/algolia/services/client"
import { getAnonUserId } from "../../../../../features/tracking/internal/storage"
import { getDefaultSortIndex, getSortIndex } from "../../../data/sorting"
import { FiltersMap, SearchState, SwitchMap } from "../../../types"

const DEFAULT_PAGE_SIZE = 16

type FacetFilters = (string | string[])[] | undefined

export interface SearchInput {
  initialFilters: SearchParameters
  searchParams: SearchState
  settings: SerpSettings
  analytics: boolean
  timestamp: Date
}

const getSwitchOptions = (map: SwitchMap) =>
  Array.from(map.values()).map((x) => `switch:${x}`)

const getBrandOptions = (brandNames: string[]) => [brandNames as string[]]

const getFilterOptions = (filters: FiltersMap): string[][] => {
  const filterItems: string[][] = []
  for (const key of filters.keys()) {
    const filter = filters.get(key)
    if (!filter) {
      continue
    }
    filterItems.push(filter.map((x) => `${key}:${x}`))
  }
  return filterItems
}

const getCategoryOptions = (codes: string[]) => {
  return [codes.map((x) => `categoryCode:${x}`)]
}

const getDiscountOptions = (values: number[]) => [
  values.map((x) => `inventory.discountPercent:${x}`),
]

const buildFacetFilters = (searchParams: SearchState): FacetFilters => {
  const filters = [
    ...(searchParams.switch ? getSwitchOptions(searchParams.switch) : []),
    ...((searchParams.brandNames?.length ?? 0) > 0
      ? getBrandOptions(searchParams.brandNames as string[])
      : []),
    ...(searchParams.filters ? getFilterOptions(searchParams.filters) : []),
    ...(searchParams.categoryCodes
      ? getCategoryOptions(searchParams.categoryCodes)
      : []),
    ...(searchParams.discountPercentValues
      ? getDiscountOptions(searchParams.discountPercentValues)
      : []),
  ]
  return filters
}

const buildNumericFilters = (searchParams: SearchState): FacetFilters => {
  return [
    ...(searchParams.price?.minPrice
      ? [`inventory.price>=${searchParams.price.minPrice}`]
      : []),
    ...(searchParams.price?.maxPrice
      ? [`inventory.price<=${searchParams.price.maxPrice}`]
      : []),
  ]
}

const getFacets = (settings: SerpSettings): string[] => [
  "switch",
  "brandName",
  "categoryCode",
  "inventory.price",
  "inventory.discountPercent",
  ...(settings?.data?.sidebar?.filters
    ?.filter((x) => x.tagCategory?.code)
    .map((x) => `filters.${x.tagCategory?.code}` as string) ?? []),
]

interface CreateFacetQueriesInput {
  filters: string
  facetFilters: FacetFilters
  numericFilters: FacetFilters
  searchParams: SearchState
  initialFilters: SearchParameters
}

const createFacetQuery = (
  filters: string,
  facet: string,
  facetFilters: FacetFilters,
  initialFilters: SearchParameters
): SearchOptions => {
  return {
    filters,
    facets: [facet],
    facetFilters: facetFilters as any,
    facetingAfterDistinct: true,
    enablePersonalization: true,
    maxFacetHits: 100,
    maxValuesPerFacet: 1000,
    hitsPerPage: 1,
    sortFacetValuesBy: "alpha",
    page: 0,
    attributesToRetrieve: [],
    attributesToHighlight: [],
    attributesToSnippet: [],
    analytics: false,
    clickAnalytics: false,
    query: initialFilters?.query ?? "",
  }
}

const includesFacet = (filters: FacetFilters, facet: string) =>
  filters?.some((item) => {
    if (Array.isArray(item)) {
      return item[0]?.startsWith(`${facet}:`)
    }
    return item.startsWith(`${facet}:`)
  })

const excludeFacet = (filters: FacetFilters, facet: string) =>
  filters?.filter((item) => {
    if (Array.isArray(item)) {
      return !item[0]?.startsWith(`${facet}:`)
    }
    return !item.startsWith(`${facet}:`)
  })

const createBrandFacetQuery = (input: CreateFacetQueriesInput) => {
  if (!includesFacet(input.facetFilters, "brandName")) {
    return undefined
  }
  return createFacetQuery(
    input.filters,
    "brandName",
    excludeFacet(input.facetFilters, "brandName"),
    input.initialFilters
  )
}

const createDiscountFacetQuery = (input: CreateFacetQueriesInput) => {
  if (!includesFacet(input.facetFilters, "inventory.discountPercent")) {
    return undefined
  }
  return createFacetQuery(
    input.filters,
    "inventory.discountPercent",
    excludeFacet(input.facetFilters, "inventory.discountPercent"),
    input.initialFilters
  )
}

const createCategoryFacetQuery = (input: CreateFacetQueriesInput) => {
  if (!includesFacet(input.facetFilters, "categoryCode")) {
    return undefined
  }
  return createFacetQuery(
    input.filters,
    "categoryCode",
    excludeFacet(input.facetFilters, "categoryCode"),
    input.initialFilters
  )
}

const createFilterFacets = (
  filter: string[],
  input: CreateFacetQueriesInput
): SearchOptions => {
  const key = filter[0].split(":")[0]
  return createFacetQuery(
    input.filters,
    key,
    excludeFacet(input.facetFilters, key),
    input.initialFilters
  )
}

const createFiltersFacets = (
  input: CreateFacetQueriesInput
): SearchOptions[] => {
  const filters = input.facetFilters?.filter(
    (x) => Array.isArray(x) && x[0]?.startsWith("filters.")
  )
  if (!filters) {
    return []
  }

  return filters.map((x) => createFilterFacets(x as string[], input))
}

const createFacetQueries = (
  input: CreateFacetQueriesInput
): SearchOptions[] => {
  const options: SearchOptions[] = []
  const brandFacet = createBrandFacetQuery(input)
  if (brandFacet) {
    options.push(brandFacet)
  }
  options.push(...createFiltersFacets(input))
  const categoryFacet = createCategoryFacetQuery(input)
  if (categoryFacet) {
    options.push(categoryFacet)
  }
  const discountFacet = createDiscountFacetQuery(input)
  if (discountFacet) {
    options.push(discountFacet)
  }
  return options
}

export const search = async (input: SearchInput) => {
  const { initialFilters, searchParams, settings, timestamp } = input
  const index =
    (searchParams.orderBy
      ? getSortIndex(searchParams.orderBy)
      : getDefaultSortIndex()) ?? PRODUCTS_INDEX_NAME

  const filters = toAlgoliaFilter(initialFilters, timestamp)
  const facetFilters = buildFacetFilters(searchParams)
  const numericFilters = buildNumericFilters(searchParams)

  return await getAlgoliaClient().search<any>([
    {
      indexName: index,
      params: {
        filters,
        facetingAfterDistinct: true,
        maxFacetHits: 100,
        maxValuesPerFacet: 1000,
        enablePersonalization:
          !input.searchParams.orderBy ||
          input.searchParams.orderBy === "weight",
        hitsPerPage: DEFAULT_PAGE_SIZE,
        sortFacetValuesBy: "alpha",
        facets: getFacets(settings),
        facetFilters: facetFilters as any,
        numericFilters: numericFilters as any,
        page: (searchParams.page ?? 1) - 1,
        query: initialFilters?.query ?? "",
        clickAnalytics: true,
        analytics: input.analytics,
        userToken: input.analytics ? (getAnonUserId() as string) : undefined,
      },
    },
    {
      // in this request we search for facet values with initial filter only
      indexName: index,
      params: {
        filters,
        facetingAfterDistinct: true,
        maxFacetHits: 100,
        maxValuesPerFacet: 1000,
        enablePersonalization: true,
        hitsPerPage: 1,
        sortFacetValuesBy: "alpha",
        facets: getFacets(settings),
        page: 0,
        query: initialFilters?.query ?? "",
      },
    },
    ...createFacetQueries({
      filters,
      facetFilters,
      numericFilters,
      searchParams,
      initialFilters,
    }).map((x) => ({
      indexName: index,
      params: x,
    })),
  ])
}
